import { useCallback, useEffect, useState } from 'react'
import { Gift, RefreshCw, Sparkles, UserPlus } from 'lucide-react'
import { foutTekst, vraag } from '../lib/api'
import { getal } from '../lib/opmaak'
import { alleVestigingen } from '../../supabase/functions/_gedeeld/regels.ts'
import { Laden, type Sessie } from './Beheer'

/*
 * Het logboek per vestiging: wie wisselde wat in, bevestigd door welke
 * medewerker. Ook de wasbeurten en de nieuwe kaarten. Een locatiemanager
 * ziet alleen zijn eigen vestigingen (dat bepaalt de server, niet dit
 * scherm). "Vandaag" ververst zichzelf elke halve minuut.
 */

interface Rij { t: string; vestiging: string; tag: string; klant: string; beloning: string; punten: number; medewerker: string }
interface Antwoord {
  soort: string; periode: string; totaal: { aantal: number; punten: number }; meer: boolean
  rijen: Rij[]; medewerkers: { id: string; naam: string }[]
}

const SOORTEN = [
  { k: 'inwisselingen', naam: 'Inwisselingen', icoon: <Gift size={16} /> },
  { k: 'scans', naam: 'Wasbeurten', icoon: <Sparkles size={16} /> },
  { k: 'registraties', naam: 'Nieuwe kaarten', icoon: <UserPlus size={16} /> },
] as const
const PERIODES = [{ k: 0, naam: 'Vandaag' }, { k: 7, naam: '7 dagen' }, { k: 30, naam: '30 dagen' }, { k: 90, naam: '90 dagen' }]

const dag = (n: number) => new Date(Date.now() - n * 86400_000).toLocaleDateString('sv-SE', { timeZone: 'Europe/Amsterdam' })
const tijd = (iso: string, metDatum: boolean) => new Intl.DateTimeFormat('nl-NL', {
  timeZone: 'Europe/Amsterdam', hour: '2-digit', minute: '2-digit', ...(metDatum ? { day: 'numeric', month: 'short' } : {}),
}).format(new Date(iso))

export function Logboek({ s }: { s: Sessie }) {
  const [soort, zetSoort] = useState<typeof SOORTEN[number]['k']>('inwisselingen')
  const [dagen, zetDagen] = useState(0)
  const [vestiging, zetVestiging] = useState('')
  const [medewerker, zetMedewerker] = useState('')
  const [data, zetData] = useState<Antwoord | null>(null)
  const [extra, zetExtra] = useState<Rij[]>([])
  const [fout, zetFout] = useState<string | null>(null)
  const [laadt, zetLaadt] = useState(false)

  const mijn = s.vestigingen.filter((v) => alleVestigingen(s.ik.rol) || s.ik.vestigingen.includes(v.id))
  const filters = useCallback(() => ({
    soort, van: dag(dagen), tot: dag(0), vestiging: vestiging || undefined,
    medewerker: soort === 'inwisselingen' && medewerker ? medewerker : undefined,
  }), [soort, dagen, vestiging, medewerker])

  const laad = useCallback(async () => {
    zetLaadt(true); zetFout(null)
    try { zetData(await vraag<Antwoord>('logboek', filters())); zetExtra([]) } catch (e) { zetFout(foutTekst(e)) } finally { zetLaadt(false) }
  }, [filters])
  useEffect(() => { laad() }, [laad])
  useEffect(() => {
    if (dagen !== 0) return
    const t = setInterval(laad, 30_000)
    return () => clearInterval(t)
  }, [dagen, laad])

  const meer = async () => {
    const alle = [...(data?.rijen ?? []), ...extra]
    const laatste = alle[alle.length - 1]
    if (!laatste) return
    try {
      const r = await vraag<Antwoord>('logboek', { ...filters(), voor: laatste.t })
      zetExtra([...extra, ...r.rijen])
      zetData((d) => d && { ...d, meer: r.meer })
    } catch (e) { zetFout(foutTekst(e)) }
  }

  const rijen = [...(data?.rijen ?? []), ...extra]
  const metDatum = dagen > 0
  const meerdereVestigingen = mijn.length > 1

  return (
    <>
      <div className="pagina-kop">
        <h2>Logboek</h2>
        <button className="knop stil-knop klein-knop" onClick={laad} disabled={laadt}><RefreshCw size={16} className={laadt ? 'draai' : ''} />Vernieuwen</button>
      </div>

      <div className="chips" role="group" aria-label="Soort">
        {SOORTEN.map((x) => <button key={x.k} className="chip" aria-pressed={soort === x.k} onClick={() => zetSoort(x.k)}>{x.icoon}{x.naam}</button>)}
      </div>
      <div className="chips" role="group" aria-label="Periode">
        {PERIODES.map((x) => <button key={x.k} className="chip" aria-pressed={dagen === x.k} onClick={() => zetDagen(x.k)}>{x.naam}</button>)}
      </div>
      {(meerdereVestigingen || soort === 'inwisselingen') && (
        <div className="twee-kol" style={{ marginBottom: 14 }}>
          {meerdereVestigingen && (
            <label className="veld"><span>Vestiging</span>
              <select value={vestiging} onChange={(e) => zetVestiging(e.target.value)}>
                <option value="">Alle{alleVestigingen(s.ik.rol) ? '' : ' van mij'}</option>
                {mijn.map((v) => <option key={v.id} value={v.id}>{v.naam.replace(/^Truckwash\s*/i, '')}</option>)}
              </select>
            </label>
          )}
          {soort === 'inwisselingen' && (
            <label className="veld"><span>Medewerker</span>
              <select value={medewerker} onChange={(e) => zetMedewerker(e.target.value)}>
                <option value="">Iedereen</option>
                {(data?.medewerkers ?? []).map((m) => <option key={m.id} value={m.id}>{m.naam}</option>)}
              </select>
            </label>
          )}
        </div>
      )}

      {data && (
        <div className="tegels" style={{ gridTemplateColumns: 'repeat(2, minmax(0, 1fr))' }}>
          <div className="kaart tegel"><small>{SOORTEN.find((x) => x.k === soort)?.naam}</small><b>{getal(data.totaal.aantal)}</b><span>{PERIODES.find((x) => x.k === dagen)?.naam.toLowerCase()}</span></div>
          {soort === 'inwisselingen' && <div className="kaart tegel"><small>Punten</small><b>{getal(data.totaal.punten)}</b><span>{PERIODES.find((x) => x.k === dagen)?.naam.toLowerCase()}</span></div>}
        </div>
      )}

      {fout && <div className="melding fout" style={{ marginBottom: 12 }}>{fout}</div>}
      {!data ? <Laden fout={fout} opnieuw={laad} /> : rijen.length === 0 ? (
        <div className="leeg-staat">Niets in deze periode.</div>
      ) : (
        <div className="kaart" style={{ padding: '4px 0' }}>
          <table className="tabel kaartjes">
            <thead><tr>
              <th>{metDatum ? 'Wanneer' : 'Tijd'}</th>
              {soort === 'inwisselingen' && <th>Beloning</th>}
              <th>Klant</th>
              {soort === 'inwisselingen' && <th>Bevestigd door</th>}
              {(meerdereVestigingen || !vestiging) && mijn.length > 1 && <th>Vestiging</th>}
              {soort === 'inwisselingen' && <th className="getal">Punten</th>}
            </tr></thead>
            <tbody>
              {rijen.map((r, i) => (
                <tr key={`${r.t}-${i}`}>
                  <td className="hoofd">{tijd(r.t, metDatum)}</td>
                  {soort === 'inwisselingen' && <td data-l="Beloning">{r.beloning}</td>}
                  <td data-l="Klant"><span className="code-pil">{r.tag}</span>{r.klant ? ` ${r.klant}` : ''}</td>
                  {soort === 'inwisselingen' && <td data-l="Bevestigd door">{r.medewerker || '—'}</td>}
                  {(meerdereVestigingen || !vestiging) && mijn.length > 1 && <td data-l="Vestiging">{r.vestiging.replace(/^Truckwash\s*/i, '')}</td>}
                  {soort === 'inwisselingen' && <td className="getal" data-l="Punten">{Math.abs(r.punten)}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data?.meer && <div style={{ textAlign: 'center', marginTop: 14 }}><button className="knop" onClick={meer}>Meer laden</button></div>}
    </>
  )
}
